import { Checkbox } from '@/components/ui/checkbox';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Translator } from '../translator.type';

export type Client = {
  id: string;
  ref: string;
  agencyName?: string | null;
  mainContact: {
    firstName: string;
    lastName: string;
  };
  countEstimates?: number;
  countReservations?: number;
  destination?: string;
  agent?: string;
  receiver?: string;
  createdAt: string;
  updatedAt: string;
  reservationAgent: {
    id: string;
    firstname: string;
    lastname: string;
  };
  projects: {
    _count: { estimates: number; reservations: number };
  }[];
};

export const getClientsColumnDef = (
  t: Translator,
  isTourOperator: boolean,
): ColumnDef<Client>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'ref',
    header: t('clients.columns.ref'),
    cell: ({ row }) => (
      <span>{`C${row.original.ref.toString().padStart(4, '0')}`}</span>
    ),
  },
  ...(isTourOperator
    ? [
        {
          accessorKey: 'agencyName',
          header: t('distributorAgencies.columns.agency'),
          cell: ({ row }: { row: { original: Client } }) => (
            <span>{row.original.agencyName ?? '-'}</span>
          ),
        },
      ]
    : [
        {
          accessorKey: 'mainContact.lastName',
          header: t('clients.columns.lastName'),
        },
        {
          accessorKey: 'mainContact.firstName',
          header: t('clients.columns.firstName'),
        },
      ]),
  {
    accessorKey: 'countEstimates',
    header: t('clients.columns.estimates'),
    cell: ({ row }) => (
      <span>
        {row.original.projects.reduce(
          (acc, { _count }) => acc + _count.estimates,
          0,
        )}
      </span>
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'countReservations',
    header: t('clients.columns.reservations'),
    cell: ({ row }) => (
      <span>
        {row.original.projects.reduce(
          (acc, { _count }) => acc + _count.reservations,
          0,
        )}
      </span>
    ),
    enableSorting: false,
  },
  {
    accessorKey: 'destination',
    header: t('clients.columns.destination'),
    cell: () => '-',
    enableSorting: false,
  },
  {
    accessorKey: 'reservationAgent',
    header: t('clients.columns.agent'),
    cell: ({ row }) =>
      `${row.original.reservationAgent.firstname} ${row.original.reservationAgent.lastname}`,
    enableSorting: false,
  },
  {
    accessorKey: 'receiver',
    header: t('clients.columns.receiver'),
    cell: () => '-',
    enableSorting: false,
  },
  {
    accessorKey: 'createdAt',
    header: t('clients.columns.createdAt'),
    cell: ({ row }) => (
      <span>{format(new Date(row.original.createdAt), 'dd/MM/yyyy')}</span>
    ),
  },
  {
    accessorKey: 'updatedAt',
    header: t('clients.columns.updatedAt'),
    cell: ({ row }) => (
      <span>{format(new Date(row.original.updatedAt), 'dd/MM/yyyy')}</span>
    ),
  },
];

/** @deprecated Use getClientsColumnDef(t, false) */
export const CLIENTS_COLUMN_DEF = (t: Translator) =>
  getClientsColumnDef(t, false);
